import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useCopilotChatInternal } from '@copilotkit/react-core';
import { Mentions } from 'antd';
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiApp } from '@/config/context';
import { ENV } from '@/infrastructure/env-config';
import { isVersion } from '@/infrastructure/utils/version-strategy';
import { useAgentContext } from './agent';
import { routeToAgent, AGENT_ROUTE_CONFIG } from './agent-router';
import { AgentTools } from './agent-tools';
import styles from './copilot-input.module.scss';
import { FileItem, ExtendedFile } from './file-item';

// icons
import DesktopIcon from './icons/desktop.svg?react';
import SendDisabledIcon from './icons/send-disabled.svg?react';
import SendIcon from './icons/send.svg?react';
import StopIcon from './icons/stop.svg?react';


export interface CopilotInputProps {
  inProgress: boolean;
  onSend: (text: string) => Promise<any>;
  isVisible?: boolean;
  onStop?: () => void;
  onUpload?: () => void;
  hideStopButton?: boolean;
  // Agent 相关 props
  availableAgents?: string[];
  currentAgent?: string;
  onAgentChange?: (agent: string, message: string) => void;
  onResetSession?: () => void;
  onBeforeSend?: (text: string, files: ExtendedFile[]) => Promise<void>;
  onAddFiles?: (files: File[]) => void;
}

export const CopilotInput = (props: CopilotInputProps) => {
  const { t } = useTranslation();
  const {
    onSend,
    inProgress,
    availableAgents = [],
    currentAgent,
    onAgentChange,
    onBeforeSend,
    onAddFiles,
  } = props;
  const { stopGeneration } = useCopilotChatInternal();
  const {
    agentOptions,
    quickText,
    setQuickText,
    files,
    setFiles,
    agents,
    handleRemoveAgent,
    handleOpenAgentDrawer,
  } = useAgentContext();

  const [text, setText] = useState('');
  const [routing, setRouting] = useState(false);
  const isMountedRef = useRef(true);
  const mentionsRef = useRef<any>(null);
  const rootRef = useRef(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileListRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [needScroll, setNeedScroll] = useState(false);

  // 获取可用的agent选项，转换为 antd Mentions 格式
  const mentionOptions = useMemo(
    () => agentOptions
      .filter(agent => availableAgents.includes(agent.value))
      .map(agent => ({
        value: agent.label,
        label: agent.label,
        extra: agent,
      })),
    [agentOptions, availableAgents],
  );

  // 组件卸载时清理
  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  // 处理文本变化 - 使用 useCallback 避免重新创建
  const handleTextChange = useCallback((value: string) => {
    setText(value);
  }, []);

  // 处理选择 mention - 使用 useCallback 避免重新创建
  const handleMentionSelect = useCallback((option: any) => {
    onSelectedAgent(option.key);
  }, []);

  const clearText = useCallback(() => {
    setText('');
    setQuickText('');
    setFiles([]);
  }, [setQuickText, setFiles]);

  const onSendMessage = async (message?: string) => {
    const currentText = message || text;

    // 文件和内容必须有一个存在
    const isEmptyInput = !currentText && !files.length;

    if (isEmptyInput || inProgress || routing) return;

    try {
      await onBeforeSend?.(currentText, files);
    } catch (e) {
      clearText();
    }

    // 检查是否有 @ 提及的 agent
    const selectedAgent = window.__AT_AGENT;
    const messageContent = currentText;

    // 如果有选中的agent
    if (selectedAgent) {
      // 直接使用选中的agent，跳过路由
      if (selectedAgent !== currentAgent && onAgentChange) {
        onAgentChange(selectedAgent, messageContent);
      } else {
        // await onSend(messageContent);
      }

      clearText();
      onSelectedAgent('');
      return;
    }

    // 清空输入框
    clearText();

    const availableAgentItems = agentOptions.filter(item => availableAgents.includes(item.value));

    // 如果没有可用 agents 或只有一个 agent，直接发送
    if (!availableAgents || availableAgents.length <= 1) {
      onAgentChange?.(availableAgents?.[0], messageContent);
      if (isMountedRef.current) {
        clearText();
      }
      return;
    }

    try {
      // 1. 开始路由
      if (isMountedRef.current) {
        setRouting(true);
      }

      // 2. 调用 LLM 分类 API 确定目标 agent
      const routedAgent = await Promise.race([
        routeToAgent(messageContent, availableAgentItems,  { // 使用过滤后的内容
          // 可以传递额外的上下文信息
          userId: '', // TODO: 从上下文获取
          productId: '', // TODO: 从上下文获取
          sessionId: '', // TODO: 从上下文获取
        }),
        // 超时保护
        new Promise<string>((_, reject) => setTimeout(() => reject(new Error('Agent routing timeout')), AGENT_ROUTE_CONFIG.timeout)),
      ]);

      onAgentChange?.(routedAgent, messageContent); // 使用过滤后的内容

      // 4. 发送消息
      // await onSend(messageContent);
      if (isMountedRef.current) {
        clearText();
      }
    } catch (error) {
      // 特殊处理 AbortError - 这通常发生在请求被取消时
      if (error instanceof Error && error.name === 'AbortError') {
        if (isMountedRef.current) {
          clearText();
        }
        return;
      }

      // 降级处理：直接发送消息
      try {
        await onSend(messageContent); // 使用过滤后的内容
        if (isMountedRef.current) {
          clearText();
        }
      } catch (sendError) {
        // 也要处理 sendError 中的 AbortError
        if (sendError instanceof Error && sendError.name === 'AbortError') {
          if (isMountedRef.current) {
            clearText();
          }
          return;
        }

        if (isMountedRef.current && AGENT_ROUTE_CONFIG.showLoadingState) {

        }
      }
    } finally {
      clearText();
      if (isMountedRef.current) {
        setRouting(false);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // 如果正在输入法输入过程中，不处理 Enter（让输入法完成候选词确认）
    if (e.nativeEvent.isComposing || mentionsRef.current?.state?.measuring) {
      return;
    }

    // Ctrl/Cmd + Enter 换行
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      return;
    }

    // Shift + Enter 换行
    if (e.key === 'Enter' && e.shiftKey) {
      // 允许默认换行行为
      return;
    }

    // 普通 Enter 键发送
    if (e.key === 'Enter') {
      e.preventDefault();
      onSendMessage();
      return;
    }
  };

  const handleMentionKeyDown = (e: React.KeyboardEvent) => {
    handleKeyDown(e as React.KeyboardEvent<HTMLTextAreaElement>);
  };

  const onSelectedAgent = useCallback((agent: string) => {
    window.__AT_AGENT = agent;
  }, []);

  const onCreateAgent = () => {
    // 根据环境选择跳转域名
    const targetDomain = ENV.isProd ? 'buglyagent.woa.com' : 't.buglyagent.woa.com';
    const targetUrl = `https://${targetDomain}`;

    // 新窗口打开
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  };

  // 检查文件列表滚动状态 - 使用 useCallback 避免重新创建
  const checkFileListScrollStatus = useCallback(() => {
    if (fileListRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = fileListRef.current;
      const hasOverflow = scrollWidth > clientWidth;
      setNeedScroll(hasOverflow);
      setCanScrollLeft(hasOverflow && scrollLeft > 0);
      setCanScrollRight(hasOverflow && scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  // 文件列表左滚动 - 使用 useCallback 避免重新创建
  const scrollFileListLeft = useCallback(() => {
    if (fileListRef.current) {
      fileListRef.current.scrollBy({ left: -150, behavior: 'smooth' });
    }
  }, []);

  // 文件列表右滚动 - 使用 useCallback 避免重新创建
  const scrollFileListRight = useCallback(() => {
    if (fileListRef.current) {
      fileListRef.current.scrollBy({ left: 150, behavior: 'smooth' });
    }
  }, []);

  // 监听文件列表滚动
  useEffect(() => {
    const fileList = fileListRef.current;
    if (fileList && files.length > 0) {
      fileList.addEventListener('scroll', checkFileListScrollStatus);
      window.addEventListener('resize', checkFileListScrollStatus);

      // 初始检查
      checkFileListScrollStatus();

      return () => {
        fileList.removeEventListener('scroll', checkFileListScrollStatus);
        window.removeEventListener('resize', checkFileListScrollStatus);
      };
    }
    return undefined;
  }, [files.length, checkFileListScrollStatus]); // 改为 files.length 而不是 files

  // 当文件列表变化时重新检查滚动状态
  useEffect(() => {
    setTimeout(checkFileListScrollStatus, 100);
  }, [files.length, checkFileListScrollStatus]); // 改为 files.length 而不是 files

  // 处理文件选择 - 使用 useCallback 避免重新创建
  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const fileArray = Array.from(selectedFiles);
      onAddFiles?.(fileArray);
    }
    // 清空input值，以便可以重复选择相同文件
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onAddFiles]);

  // 触发文件选择 - 使用 useCallback 避免重新创建
  const handleAddFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const renderFileList = useCallback(() => files.map(extendedFile => (
    <FileItem
      key={extendedFile.id}
      extendedFile={extendedFile}
      onDelete={(id: string) => {
        const newFiles = files.filter((f: ExtendedFile) => f.id !== id);
        setFiles(newFiles);
      }}
      onStatusChange={(id: string, updates: Partial<ExtendedFile>) => {
        setFiles((prevFiles: ExtendedFile[]) => prevFiles
          .map((f: ExtendedFile) => (f.id === id ? { ...f, ...updates } : f)));
      }}
    />
  )), [files, setFiles]);

  useEffect(() => {
    if (quickText) {
      // 先清空，再发送，避免在发送过程中quickText还保持旧值
      const textToSend = quickText;
      setQuickText('');
      onSendMessage(textToSend);
    }
  }, [quickText]);

  return (
    <div className={styles.copilotInputContainer} style={{ position: 'relative' }} ref={rootRef}>
     <div className={styles.copilotInput}>
      {/* Agent Tools */}
      <AgentTools
        agents={agents}
        agentOptions={agentOptions}
        onRemoveAgent={handleRemoveAgent}
        onOpenAgentDrawer={handleOpenAgentDrawer}
      />

      <div className={styles.fileListWrapper}>
        {needScroll && (
          <div
            className={`${styles.fileScrollArrow} ${styles.left} ${!canScrollLeft ? styles.disabled : ''}`}
            onClick={scrollFileListLeft}
          >
            <LeftOutlined />
          </div>
        )}
        <div className={styles.fileListContainer}>
          <div className={styles.fileList} ref={fileListRef}>
            {renderFileList()}
            {/* 添加文件按钮 */}
            {files.length > 0 && <div className={styles.addFileBtn} onClick={handleAddFileClick}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 4.16669V15.8334M4.16669 10H15.8334" stroke="#3385FF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>}
          </div>
        </div>
        {needScroll && (
          <div
            className={`${styles.fileScrollArrow} ${styles.right} ${!canScrollRight ? styles.disabled : ''}`}
            onClick={scrollFileListRight}
          >
            <RightOutlined />
          </div>
        )}
        {/* 隐藏的文件input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileInputChange}
          accept=".xlog,.log,.txt,.zip"
        />
      </div>
      <Mentions
        ref={mentionsRef}
        className={styles.copilotMentions}
        placeholder={t('请描述希望分析的数据，使用 @agent名称 指定智能体 | Shift+Enter 换行，Enter 发送')}
        value={text}
        onChange={handleTextChange}
        onKeyDown={handleMentionKeyDown}
        onSelect={handleMentionSelect}
        disabled={routing || inProgress}
        // getPopupContainer={() => rootRef.current as any}
        dropdownClassName={styles.copilotMentionsDropdown}
        // autoSize={{ minRows: 3, maxRows: 4 }}
        rows={3}
        style={{
          width: '100%',
        }}
      >
        {mentionOptions.map(option => (
          <Mentions.Option key={option.extra.value} value={option.value}>
            {option.label}
          </Mentions.Option>
        ))}
      </Mentions>

      <div className={styles.copilotInputActions}>
        {isVersion(MultiApp.WOA) && (
          <span onClick={onCreateAgent} style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            border: '1px solid #D2DAE6',
            width: 28,
            height: 28,
            justifyContent: 'center',
            borderRadius: '50%',
          }}>
            <DesktopIcon />
          </span>
        )}
        {(!inProgress && (text || files.length > 0) && !routing) && (
          <span onClick={() => onSendMessage()} style={{ cursor: 'pointer' }}>
            {<SendIcon />}
          </span>
        )}
        {(!text && files.length === 0 && !inProgress) && (
            <span style={{ cursor: 'pointer' }}>
                {<SendDisabledIcon />}
            </span>
        )}
        {inProgress && (
          <span onClick={() => stopGeneration()} style={{ cursor: 'pointer' }}>
            {<StopIcon />}
          </span>
        )}
      </div>
     </div>
    </div>
  );
};
